import FeaturedEventChooser from 'src/services/featuredEventChooser';
import Navigation from 'src/models/Navigation';
import { defaultHitsPerPage } from '../utils/pagination';
import PluginApi from '../apis/./pluginApi';
import FeatureFlags from 'src/services/FeatureFlags';

/*
  Event Search Wrapper
 */
export default class EventSearch {
  constructor({ context, pluginConfig }) {
    this.pluginConfig = pluginConfig;
    this.publisherSettings = pluginConfig.publisherSettings;
    this.frameConfig = pluginConfig.frameConfig;
    this.pluginApi = new PluginApi();
    this.flagClient = new FeatureFlags();

    const searchParams = {};

    // This means that the plugin is category specific. All queries will be scoped to this category.
    if (this.frameConfig.options.category_id)  {
      searchParams.categoryId = this.frameConfig.options.category_id;

      // This is a bit tricky, when the plugin is category specific, the navigation categories become subcategories.
      if (context.query.category) {
        searchParams.subCategoryIds = new Navigation(pluginConfig).lookupBySlug(
          context.query.category
        )?.id;
      }

    } else if (context.query.category) {
      searchParams.categoryId = new Navigation(pluginConfig).lookupBySlug(
        context.query.category
      )?.id;
    }

    if (context.query.subcategory) {
      const subCategoriesList = context.query.subcategory.split('^');
      searchParams.subCategoryIds = subCategoriesList.map((subCategory) => {
        return subCategory.split('|')[0];
      });
    }

    if (context.query.towns) {
      searchParams.towns = context.query.towns;
    }

    if (context.query.countries) {
      searchParams.countries = context.query.countries;
    }

    if (context.query.searchTerm) {
      searchParams.searchTerm = context.query.searchTerm;
    } else if (context.query.query) {
      searchParams.searchTerm = context.query.query;
    }

    if (context.query.event) {
      searchParams.eventId = context.query.event.split('-')[0];
    }

    if (context.query.fromDate) {
      searchParams.fromDate = context.query.fromDate;
    }

    if (context.query.toDate) {
      searchParams.toDate = context.query.toDate;
    }

    if (context.query.virtual) {
      searchParams.virtual = context.query.virtual;
    }

    searchParams.multipleEventInstances = true;
    searchParams.publisher_id = this.publisherSettings.publisher.id;
    searchParams.hitsPerPage = defaultHitsPerPage();

    this.searchParams = searchParams;
  }

  async checkFlag(flag) {
    const flagValue = await this.flagClient.checkFlag({
      flagName: flag,
      context: { publisher_id: this.publisherSettings.publisher.id },
    });
    return flagValue;
  }

  async getApiClient(flag) {
    const useNewApi = await this.checkFlag(flag);
    return useNewApi ? this.pluginApi : this.legacyApi;
  }

  // This function performs queries to Algolia to get the events, featured events and base facets.
  // It is now possible to start from a page other than 0, so we need to parse the page number
  async homePageEvents(page = 0) {
    const apiClient = await this.getApiClient('home-page-api');
    const search = await apiClient.getHomePageEvents(
      this.publisherSettings.publisher.id,
      {
        ...this.searchParams,
        page,
      }
    );

    const { rawEvents, rawFacets, rawFeaturedEvents, rawEditorsPickEvents } = search;

    return {
      rawEvents,
      rawFacets,
      rawFeaturedEvents,
      rawEditorsPickEvents,
    };
  }

  async moreEvents(page) {
    const apiClient = await this.getApiClient('get-events-api');
    const search = await apiClient.getEvents({
      ...this.searchParams,
      page,
    });

    return search.events;
  }

  async search({ searchTerm, page, hitsPerPage }) {
    let searchData;

    if (await this.checkFlag('new-search')) {
      searchData = await this.pluginApi.search({
        ...this.searchParams,
        page: page || 0,
        searchTerm,
        hitsPerPage,
      });
    } else {
      searchData = await this.legacyApi.getEvents({
        ...this.searchParams,
        page: page || 0,
        searchTerm,
        hitsPerPage,
      });
    }

    return {
      events: searchData.events,
      facets: searchData.facets,
      meta: {
        nbHits: searchData.meta?.nbHits,
      },
    };
  }

  async event() {
    const searchData = await this.pluginApi.getEventById(this.searchParams.eventId, {
      publisher_id: this.publisherSettings.publisher.id,
    });

    const { rawEvent, editorialContent, baseFacets, signedStaticMapUrl } = searchData;

    return {
      rawEvent: rawEvent,
      editorialContent: editorialContent,
      rawFacets: baseFacets,
      signedStaticMapUrl,
    };
  }

  async widgetEvents({ widgetNumber = 5, categoryId }) {
    // This endpoint is heavily cached, so this is a (naive) hack to get more events to choose from.
    // We'll then use the FeaturedEventChooser to pick the right amount of events.
    // We will improve this in the future.
    const filters = { ...this.searchParams, widgetNumber: 30 };

    if (categoryId) {
      filters.categoryId = categoryId;
    }

    const apiClient = await this.getApiClient('widget-api');
    const searchData = await apiClient.getWidgetEvents(
      this.publisherSettings.publisher.id,
      filters
    );

    return new FeaturedEventChooser(searchData.rawEvents, widgetNumber).choose();
  }

  async otherOccurrences(event) {
    const searchApi = await this.getApiClient('get-events-api');
    const search = await searchApi.getEvents({
      hitsPerPage: 20,
      parentId: event.parentId,
      multipleEventInstances: true,
      notSourceId: event.data.source_id,
      publisher_id: this.publisherSettings.publisher.id,
    });

    return search.events;
  }

  async relatedFeatured(event) {
    const searchApi = await this.getApiClient('recommended-api');

    const filters = {
      hitsPerPage: 50,
      publisher_id: this.publisherSettings.publisher.id,
    };

    // TODO: This is a temporary fix to reduce calls to Algolia.
    if (event?.data) {
      const useNewApi = this.checkFlag('recommended-api');
      if (useNewApi) {
        filters.categoryId = event.data.main_category_path[0];
      }
    }
    const search = await searchApi.getRecommendedEvents(filters);
    const excludeIds = event?.data?.objectID ? [event.data.objectID] : [];

    return new FeaturedEventChooser(search.events, 10, excludeIds).choose();
  }

  get state() {
    return this.legacyApi.state();
  }

  async allFeaturedEvents(currentFeaturedEvents) {
    const searchApi = await this.getApiClient('featured-api');
    const search = await searchApi.getFeaturedEvents(this.searchParams);

    return new FeaturedEventChooser(search.events, search.events.length).chooseMore(
      currentFeaturedEvents
    );
  }

  async allEditorsPickEvents() {
    const searchApi = await this.getApiClient('editor-picks-api');
    // We'll try to get them all when getting the full amount, but limit it to 100.
    const search = await searchApi.getEditorialPickEvents(
      Object.assign({}, this.searchParams, { hitsPerPage: 100 })
    );

    return search.events;
  }
}
